import {defineStore} from 'pinia'
import {IKeyboardShortcutStoreState} from './types/IKeyboardShortcutStoreState'
import {IKeyboardShortcut} from '@toolify/client/src/stores/KeyboardShortcutStore/types/IKeyboardShortcut'
import {KeyboardModifierKeyTypes} from '@toolify/client/src/stores/KeyboardShortcutStore/enum/KeyboardModifierKeyTypes'
import {IKeyboardKeyCombination} from '@toolify/client/src/stores/KeyboardShortcutStore/types/IKeyboardKeyCombination'
import {KeyboardKeyCodeMap} from '@toolify/client/src/stores/KeyboardShortcutStore/maps/KeyboardKeyCodeMap'
import {IKeyboardShortcutItem} from './types/IKeyboardShortcutItem'
import {KeyboardSpecialKeyTypes} from '@toolify/client/src/stores/KeyboardShortcutStore/enum/KeyboardSpecialKeyTypes'
import {CommonShortcutType} from '@toolify/client/src/stores/KeyboardShortcutStore/enum/CommonShortcutType'
import {
  KeyboardNormalTypingKeysTypes,
  KeyboardSpecialTypingKeyTypes,
} from '@toolify/client/src/stores/KeyboardShortcutStore/enum/KeyboardNormalKeysTypes'
import {hasTextEntryFocus} from '@toolify/client/src/modules/keyboard/functions/textEntryFocus'

export const useKeyboardShortcutStore = defineStore('keyboardShortcut', {
  state: () => {
    return {
      currentId: 0,
      shortcuts: [],
      scopes: [],
    } as IKeyboardShortcutStoreState
  },
  actions: {
    createScope(scope: symbol) {
      this.scopes.push(scope)
    },
    deleteScope(scope: symbol) {
      this.scopes = this.scopes.filter(currentScope => {
        return currentScope !== scope
      })
    },
    registerShortcuts(shortcuts: IKeyboardShortcut[], scope: symbol): symbol[] {
      return shortcuts.map(shortcut => {
        return this.registerShortcut(shortcut, scope)
      })
    },
    registerShortcut(shortcut: IKeyboardShortcut, scope: symbol): symbol {
      const id = Symbol()
      this.shortcuts.push({
        shortcut,
        id,
        scope,
      })
      return id
    },
    deleteShortcutsByIds(ids: symbol[]): void {
      this.shortcuts = this.shortcuts.filter(shortcut => {
        return !ids.includes(shortcut.id)
      })
    },
    deleteShortcutById(id: symbol): void {
      this.deleteShortcutsByIds([id])
    },
    initializeListeners() {
      document.addEventListener('keydown', this.keyDownListener)
      // document.addEventListener('keyup', this.keyUpListener)
    },
    dispose() {
      document.removeEventListener('keydown', this.keyDownListener)
      // document.removeEventListener('keyup', this.keyUpListener)
    },
    getKeyCombinationOutOfEvent(event: KeyboardEvent): IKeyboardKeyCombination {
      const items = [
        {
          keyType: KeyboardModifierKeyTypes.Alt,
          value: event.altKey,
        },
        {
          keyType: KeyboardModifierKeyTypes.Shift,
          value: event.shiftKey,
        },
        {
          keyType: KeyboardModifierKeyTypes.Control,
          value: event.ctrlKey,
        },
        {
          keyType: KeyboardModifierKeyTypes.Command,
          value: event.metaKey,
        },
      ]
      const modifierKeys = items.filter(item => item.value).map(item => item.keyType)
      const key = this.mapKeyCodeToKey(event.code)
      return [...modifierKeys, key]
    },
    mapKeyCodeToKey(keyCode: string): string {
      return KeyboardKeyCodeMap.get(keyCode) || keyCode
    },
    isInputTypingKeyboardEvent(event: KeyboardEvent): boolean {
      if(event.ctrlKey || event.metaKey || event.altKey) {
        return false
      }
      const key = event.key
      const allTypingKeys = [
        ...Object.values(KeyboardNormalTypingKeysTypes),
        ...Object.values(KeyboardSpecialTypingKeyTypes),
      ] as string[]
      return allTypingKeys.includes(key)
    },
    // A shortcut that asked to stand aside while the user types is not a candidate at all, so the
    // keystroke reaches the text entry untouched and another shortcut may still answer it.
    isShortcutListening(shortcutItem: IKeyboardShortcutItem): boolean {
      return !shortcutItem.shortcut.skipWhileTyping || !hasTextEntryFocus()
    },
    shortcutMatchesEvent(shortcutItem: IKeyboardShortcutItem, event: KeyboardEvent, eventCombination: IKeyboardKeyCombination): boolean {
      const keys = shortcutItem.shortcut.keys
      if(keys.length === 1 && keys[0] === KeyboardSpecialKeyTypes.InputTyping) {
        return this.isInputTypingKeyboardEvent(event)
      }
      return this.compareTwoKeyCombinations(eventCombination, keys)
    },
    compareTwoKeyCombinations(firstCombination: IKeyboardKeyCombination, secondCombination: IKeyboardKeyCombination): boolean {
      const sortCopy = (arr: string[]) => [...arr].sort()

      const firstSorted = sortCopy(firstCombination)
      const secondSorted = sortCopy(secondCombination)

      if ((firstCombination.length === 1 && firstCombination[0] === null) ||
      (secondCombination.length === 1 && secondCombination[0] === null)) {
        return true
      }

      if (
        firstCombination.length > 1 &&
        firstCombination[0] === null &&
        sortCopy(firstCombination.slice(1)).join(',') === secondSorted.join(',')
      ) {
        return true
      }

      if (
        secondCombination.length > 1 &&
        secondCombination[0] === null &&
        sortCopy(secondCombination.slice(1)).join(',') === firstSorted.join(',')
      ) {
        return true
      }

      return firstSorted.join(',') === secondSorted.join(',')
    },
    isNullOnlyShortcutKeys(keys: IKeyboardShortcut['keys']): boolean {
      return keys.length === 1 && keys[0] === null
    },
    pickDominantMatchingShortcut(matchingReversed: IKeyboardShortcutItem[], isTypingKeyEvent = false): IKeyboardShortcutItem {
      const nonDominated = matchingReversed.filter(candidate => {
        const t = candidate.shortcut.commonShortcutType
        if(t == null) {
          return true
        }
        return !matchingReversed.some(other => {
          return other.shortcut.moreImportantThan?.includes(t)
        })
      })
      if(nonDominated.length === 0) {
        return matchingReversed[0]
      }
      /**
       * A plain typing key (letter, digit, space, ...) belongs to the focused text field,
       * otherwise untyped shortcuts like the video player's "f" or Space would steal the
       * character. A shortcut may still win by declaring moreImportantThan InputFieldCatchAll.
       */
      const isTextFieldFocused = matchingReversed.some(item => {
        return item.shortcut.commonShortcutType === CommonShortcutType.InputFieldCatchAll &&
          this.isNullOnlyShortcutKeys(item.shortcut.keys)
      })
      const respectingFocusedField = isTypingKeyEvent && isTextFieldFocused
        ? nonDominated.filter(item => {
          return item.shortcut.commonShortcutType != null ||
            !!item.shortcut.moreImportantThan?.includes(CommonShortcutType.InputFieldCatchAll)
        })
        : nonDominated
      const preferSpecificKeys = respectingFocusedField.filter(item => {
        return !this.isNullOnlyShortcutKeys(item.shortcut.keys)
      })
      const ordered = preferSpecificKeys.length ? preferSpecificKeys : respectingFocusedField
      return ordered[0] || nonDominated[0]
    },
    keyDownListener(event: KeyboardEvent) {
      const shortcutsInCurrentScope = this.shortcuts
      const eventCombination = this.getKeyCombinationOutOfEvent(event)
      const matchingShortcuts = shortcutsInCurrentScope.filter(shortcut => {
        return this.isShortcutListening(shortcut) && this.shortcutMatchesEvent(shortcut, event, eventCombination)
      }).reverse()
      if(!matchingShortcuts.length) {
        return
      }
      // A fallback answers only a keystroke no other shortcut wants
      const primaryShortcuts = matchingShortcuts.filter(item => !item.shortcut.isFallback)
      const candidates = primaryShortcuts.length ? primaryShortcuts : matchingShortcuts
      const isTypingKeyEvent = this.isInputTypingKeyboardEvent(event)
      const winner = this.pickDominantMatchingShortcut(candidates, isTypingKeyEvent)
      winner.shortcut.callback(event)
      if(winner.shortcut.keys.includes(null)) {
        return
      }
      if(winner.shortcut.preventDefault === false) {
        return
      }
      event.preventDefault()
    },

  },
  getters: {
    currentScope(state): symbol {
      return state.scopes[state.scopes.length - 1]
    },
    shortcutsInCurrentScope(): IKeyboardShortcutItem[] {
      return this.shortcuts.filter(shortcut => {
        return shortcut.scope === this.currentScope
      })
    },
  },
})
